import React, { useMemo } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import { useLocalizedHref } from "@/libraries/web-framework";
import { Logo, ProductKeyTypes } from "@/libraries/wpl/foundations/src";

import { MobileGlobalNav } from "./mobile-nav/mobile-nav";
import GlobalNavLinksContainer, {
    GlobalNavLinkContentProps,
    GlobalNavProvider,
    SubnavLayout
} from "./nav-links-container";
import SearchAndProfile from "./nav-search-and-profile";
import { ProductSubnav } from "./product-subnav";
import { ResourcesSubnav } from "./resources-subnav";
import SearchBar from "./search-bar";
import { TeamsSubnav } from "./teams-subnav";
import { WhyAtlassianSubnav } from "./why-atlassian-subnav";
// TODO: this is a copy fron v1 of global nav need to be updated
const navigationBarWrapperCss = css({
    "alignItems": "flex-start",
    "background": "#ffffff",
    "display": "flex",
    "flexDirection": "row",
    "justifyContent": "center",
    "position": "relative",
    "zIndex": 5,
    [media.below.md]: {
        ".showMenu &": {
            background: "#f4f5f7",
            height: "100vh",
            overflow: "auto",
            position: "fixed",
            insetBlockStart: 0,
            width: "100%"
        }
    },
    ".logged-in-out-user-profile": {
        border: "thin solid #0052cc",
        borderRadius: "25px 25px 25px 25px"
    },
    ".navigation-bar": {
        alignItems: "center",
        display: "flex",
        flex: 1,
        flexDirection: "row",
        padding: `0 ${token("space.200")}`,
        height: "56px",
        position: "relative",
        zIndex: 1,
        [media.above.md]: {
            height: "72px",
            maxWidth: "1200px"
        }
    },
    ".navigation-bar-logo": {
        "display": "flex",
        "alignItems": "center",
        "width": "100%",
        [media.above.md]: {
            width: "auto"
        },
        ".navigation-bar-logo-link": {
            "display": "flex",
            "> svg": {
                height: "auto",
                transition: "opacity 250ms",
                width: "161px",
                [media.above.md]: {
                    width: "193px",
                    marginInlineEnd: "12px"
                }
            }
        }
    },

    ".navigation-links": {
        "display": "flex",
        "flex": 1,
        "flexDirection": "row",
        "listStyle": "none",
        "margin": 0,
        "padding": 0,
        ".chevron-down-icon": {
            display: "block",
            color: "#0052cc",
            height: "24px"
        },
        "> li.nav-link": {
            "> a.nav-link-anchor": {
                "alignItems": "center",
                "color": "#42526e",
                "display": "flex",
                "textDecoration": "none",
                "&.isActive": {
                    color: "#0052cc"
                }
            },
            [media.above.md]: {
                "> a.nav-link-anchor": {
                    borderRadius: "3px",
                    padding: token("space.025"),
                    paddingInlineStart: token("space.150"),
                    textDecoration: "none",
                    marginInlineEnd: "2px"
                }
            }
        },
        [media.above.md]: {
            marginInlineStart: token("space.100")
        }
    },

    ".navigation-search": {
        display: "flex"
    },

    ".navigation-action": {
        "alignItems": "center",
        "display": "flex",
        "flexDirection": "row",
        "listStyle": "none",
        "margin": 0,
        "padding": 0,
        "> li": {
            alignItems: "center",
            display: "flex",
            flexDirection: "row",
            paddingInlineStart: "12px"
        }
    },

    ".navigation-switcher-divider": {
        borderInlineStart: "1px solid #dfe1e5",
        width: "1px",
        height: "24px",
        marginInlineEnd: token("space.100")
    },

    ".navigation-cart-button": {
        "paddingInlineStart": "0 !important",
        "> div": {
            marginInlineStart: "20px",
            height: "30px",
            insetBlockStart: "8px",
            position: "relative"
        },
        "> div:empty": {
            margin: 0,
            height: 0,
            insetBlockStart: 0
        }
    }
});

const mobileWrapperCss = css({
    ".mobile-nav": {
        display: "none"
    },

    [media.below.sm]: {
        ".desktop-nav": {
            display: "none"
        },

        ".mobile-nav": {
            display: "block"
        }
    }
});

type GlobalNavLinksProps = {
    isShowTryNowLink?: boolean;
    productKey?: ProductKeyTypes;
    isShowSearchButton?: boolean;
    setIsOpenSearch: React.Dispatch<React.SetStateAction<boolean>>;
    isShowSearchBarAndProfileContainer: boolean;
};

export type GlobalNavProps = {
    productKey: ProductKeyTypes;
    isShowSearchBarAndProfileContainer?: boolean;
    isShowTryNowLink?: boolean;
    isShowSearchButton?: boolean;
};

const GlobalNavLinks = ({
    isShowTryNowLink = false,
    productKey = "jiraSoftware",
    isShowSearchButton = true,
    setIsOpenSearch,
    isShowSearchBarAndProfileContainer = true
}: GlobalNavLinksProps) => {
    const intl = useIntl();

    const enterpriseHref = useLocalizedHref("/enterprise");

    const globalLinksProps: GlobalNavLinkContentProps[] = useMemo(
        () => [
            {
                globalLinksKey: "products",
                linkText: intl.formatMessage({
                    id: "globalNavigation.Navlink.products",
                    defaultMessage: "Products"
                }),
                hasSubnav: true,
                content: <ProductSubnav />,
                layout: SubnavLayout.Product
            },
            {
                globalLinksKey: "teams",
                linkText: intl.formatMessage({
                    id: "globalNavigation.Navlink.teams",
                    defaultMessage: "Teams"
                }),
                hasSubnav: true,
                content: <TeamsSubnav />,
                layout: SubnavLayout.Teams
            },
            {
                globalLinksKey: "why-atlassian",
                linkText: intl.formatMessage({
                    id: "globalNavigation.Navlink.whyAtlassian",
                    defaultMessage: "Why Atlassian?"
                }),
                hasSubnav: true,
                content: <WhyAtlassianSubnav />,
                layout: SubnavLayout.WhyAtlassian
            },
            {
                globalLinksKey: "resources",
                linkText: intl.formatMessage({
                    id: "globalNavigation.Navlink.resources",
                    defaultMessage: "Resources"
                }),
                hasSubnav: true,
                content: <ResourcesSubnav />,
                layout: SubnavLayout.Resources
            },
            {
                globalLinksKey: "enterprise",
                linkText: intl.formatMessage({
                    id: "globalNavigation.Navlink.enterprise",
                    defaultMessage: "Enterprise"
                }),
                hasSubnav: false,
                href: enterpriseHref
            }
        ],
        [enterpriseHref, intl]
    );

    const itemList = useMemo(() => {
        return globalLinksProps.map((link) => link.globalLinksKey);
    }, [globalLinksProps]);

    return (
        <>
            <GlobalNavProvider itemList={itemList}>
                <GlobalNavLinksContainer globalLinksProps={globalLinksProps} />
            </GlobalNavProvider>
            {isShowSearchBarAndProfileContainer && (
                <SearchAndProfile
                    isShowTryNowLink={isShowTryNowLink}
                    productKey={productKey}
                    isShowSearchButton={isShowSearchButton}
                    setIsOpenSearch={setIsOpenSearch}
                />
            )}
        </>
    );
};

export const GlobalNav = ({
    productKey = "home",
    isShowSearchBarAndProfileContainer = true,
    isShowTryNowLink = false,
    isShowSearchButton = true
}: GlobalNavProps) => {
    const intl = useIntl();
    const [isOpenSearch, setIsOpenSearch] = React.useState(false);

    return (
        <div css={mobileWrapperCss}>
            <div css={navigationBarWrapperCss} className="desktop-nav">
                <div className="navigation-bar">
                    <div className="navigation-bar-logo">
                        <a
                            className="navigation-bar-logo-link"
                            href={useLocalizedHref("/")}
                            aria-label={intl.formatMessage({
                                id: "wacGlobalNavigation.logo-label",
                                defaultMessage: "Atlassian logo"
                            })}
                            data-event="clicked"
                            data-event-component="linkButton"
                            data-event-container="globalNavWac"
                            data-product-key={productKey}
                        >
                            <Logo
                                brand="atlassian"
                                type="logo"
                                appearance="brand"
                            />
                        </a>
                    </div>
                    {isOpenSearch ? (
                        <SearchBar setIsOpenSearch={setIsOpenSearch} />
                    ) : (
                        <GlobalNavLinks
                            setIsOpenSearch={setIsOpenSearch}
                            isShowSearchBarAndProfileContainer={
                                isShowSearchBarAndProfileContainer
                            }
                            productKey={productKey}
                            isShowTryNowLink={isShowTryNowLink}
                            isShowSearchButton={isShowSearchButton}
                        />
                    )}
                </div>
            </div>
            <div className="mobile-nav">
                <MobileGlobalNav productKey={productKey} />
            </div>
        </div>
    );
};

GlobalNav.displayName = "GlobalNav";
